import { useEffect, useRef, useState } from 'react';
import {
  Button,
  ButtonSize,
  ButtonWidth,
  Drawer,
  Input,
  InputTypes,
  InputValidations,
  MLPVersion,
  Notification,
  PromotionalBanner,
  PromotionalMessageTextSize,
  PromotionalMessageType,
  Theme,
  useToast,
  useTranslation,
} from '@nextgen-web-framework/all';
import { formatDate, InvalidCouponsResponse } from '@commerce-promotion-webcomponents/sdk';
import { CouponsDrawerProps } from './types/propTypes';
import styles from './CouponsDrawer.module.scss';

const CouponsDrawer = ({
  couponsList,
  appliedCouponsList,
  appliedCoupons = [],
  applyCoupon,
  closeDrawer,
}: CouponsDrawerProps) => {
  const [couponCode, setCouponCode] = useState<string>('');
  const [couponCodeError, setCouponCodeError] = useState<string>();
  const [isApplyBtnEnable, setIsApplyBtnEnable] = useState(false);
  const [selectedCoupons, setSelectedCoupons] = useState<Set<string>>(new Set());
  const isSaving = useRef(false);
  const { addToast, deleteToast } = useToast();
  const t = useTranslation();
  const invalidCouponMsg = t('promotion.coupons.invalidCoupon');
  const couponAppliedSuccess = t('promotion.coupons.couponAppliedSuccess');
  const couponRemovedMsg = t('promotion.coupons.couponRemoved');
  const couponsUpdatedMsg = t('promotion.coupons.couponsUpdated');
  const couponNotAvailableMsg = t('promotion.coupons.couponNotAvailable');
  const serverError = t('promotion.coupons.serverError');

  const sections = [
    {
      title: t('promotion.coupons.applied'),
      items: appliedCouponsList,
    },
    {
      title: t('promotion.coupons.available'),
      items: couponsList,
    },
  ];

  useEffect(() => {
    if (appliedCoupons.length) {
      setSelectedCoupons((prev) => new Set([...prev, ...appliedCoupons]));
    }
  }, [appliedCoupons]);

  const handleCouponCodeChange = (value: string) => {
    setCouponCode(value);
    setCouponCodeError(undefined);
    setIsApplyBtnEnable(!!value);
  };

  const handleApplyClick = async (e: React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element>) => {
    e.preventDefault();
    setIsApplyBtnEnable(false);
    try {
      const res = await applyCoupon([...selectedCoupons, couponCode]);
      let invalidCoupon: InvalidCouponsResponse | undefined;
      if (res && res.length > 0) {
        invalidCoupon = res.find((coupon) => coupon.couponCode == couponCode);
      }
      if (invalidCoupon) {
        setCouponCodeError(invalidCoupon?.message?.text || invalidCouponMsg);
        applyCoupon(appliedCoupons); // re-apply applied coupons to remove invalid coupons
      } else {
        setCouponCode('');
        notify();
      }
    } catch {
      notify(true);
      setIsApplyBtnEnable(true);
    }
  };

  const handlePromotionBannerClick = (id: string) => {
    const items = new Set([...selectedCoupons]);
    if (items.has(id)) {
      items.delete(id);
    } else {
      items.add(id);
    }
    setSelectedCoupons(items);
  };

  const handleConfirmClick = async () => {
    if (isSaving.current) {
      return;
    }
    const { applied, removed, message } = compareSelectedCoupons();
    if (applied == 0 && removed == 0) {
      closeDrawer();
      return;
    }
    try {
      isSaving.current = true;
      const res = await applyCoupon([...selectedCoupons]);
      if (res && res.length > 0) {
        notify(false, couponNotAvailableMsg);
        applyCoupon(appliedCoupons); // re-apply applied coupons to remove invalid coupons
      } else {
        notify(false, message);
      }
      closeDrawer();
    } catch {
      isSaving.current = false;
      notify(true);
    }
  };

  const compareSelectedCoupons = () => {
    let removed = 0;
    let applied = 0;
    const selected = [...selectedCoupons];
    const length = Math.max(selected.length, appliedCoupons.length);
    for (let i = 0; i < length; i++) {
      if (i < appliedCoupons.length && !selected.includes(appliedCoupons[i])) {
        removed++;
      }
      if (i < selected.length && !appliedCoupons.includes(selected[i])) {
        applied++;
      }
    }
    let message: string | undefined = undefined;
    if (removed > 0 && applied == 0) {
      message = couponRemovedMsg;
    } else if (applied > 0 && removed > 0) {
      message = couponsUpdatedMsg;
    }

    return { applied, removed, message };
  };

  const notify = (errorToast = false, message = couponAppliedSuccess) => {
    if (!errorToast) {
      addToast({
        id: 'success',
        content: <Notification message={message} theme={Theme.SUCCESS} onCloseClick={() => deleteToast('success')} />,
      });
    } else {
      addToast({
        id: 'error',
        content: <Notification message={serverError} theme={Theme.ERROR} onCloseClick={() => deleteToast('error')} />,
      });
    }
  };

  return (
    <Drawer
      header={t('promotion.coupons.couponsTitle')}
      closeIcon
      closeOnBackdropClick
      onClose={closeDrawer}
      bottomGap={0}
      footer={
        <div className={styles['coupons-drawer-footer']}>
          <Button
            data-testid="coupon-confirm-btn"
            title={t('promotion.specialOffer.confirmButton')}
            buttonWidth={ButtonWidth.FULL_WIDTH}
            size={ButtonSize.SMALL}
            onClick={handleConfirmClick}
          />
        </div>
      }
    >
      <div className={styles['coupons-drawer-content']}>
        <section>
          <Input
            type={InputTypes.TEXT}
            mlpVersion={MLPVersion.TWO}
            name="apply-coupon"
            data-testid="coupon-code-test-id"
            label={t('promotion.coupons.enterCouponCode')}
            value={couponCode}
            message={couponCodeError}
            status={couponCodeError ? InputValidations.INVALID : InputValidations.DEFAULT}
            onChange={(e) => handleCouponCodeChange(e.target.value)}
            ctaTextLink={{
              id: 'apply-cta',
              text: t('promotion.coupons.apply'),
              disabled: !isApplyBtnEnable,
              theme: Theme.BLACK,
              onClick: handleApplyClick,
            }}
          />
        </section>
        <div className={styles['coupons-content']}>
          {sections.map(
            ({ items, title }) =>
              Boolean(items?.length) && (
                <section key={title} className={styles['coupons-section']}>
                  <div className={styles['section-header']}>{`${title} (${items?.length})`}</div>
                  <div className={styles['section-body']}>
                    {items?.map((coupon) => (
                      <PromotionalBanner
                        onClick={() => handlePromotionBannerClick(coupon.couponCode)}
                        promotionType={coupon.couponType}
                        iconName="ticket"
                        promotionTitleText={coupon.name as string}
                        size={PromotionalMessageTextSize.LARGE}
                        promotionlDescription={coupon.description}
                        promotionEndText={
                          coupon.couponEndDate &&
                          `${t('promotion.coupons.validUntil')} ${formatDate(coupon.couponEndDate, true)}`
                        }
                        isExpanded={true}
                        key={coupon.couponCode}
                        checkbox={{
                          id: coupon.couponCode,
                          checked: selectedCoupons.has(coupon.couponCode),
                          theme: coupon.couponType === PromotionalMessageType.APPLIED ? Theme.SUCCESS : Theme.BLUE,
                        }}
                      />
                    ))}
                  </div>
                </section>
              ),
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default CouponsDrawer;
